import React, { useEffect, useState } from 'react';
import api from '../../config/api';
import { getImageUrl } from '../../utils/cdn';
import './AdminProducts.css';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    short_description: '',
    price: '',
    compare_price: '',
    stock_quantity: 0,
    sku: '',
    category_id: '',
    is_active: true,
    is_featured: false,
    meta_title: '',
    meta_description: '',
    seo_keywords: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setProducts(response.data?.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null) {
        formDataToSend.append(key, formData[key]);
      }
    });

    // Add image file if selected
    if (imageFile) {
      formDataToSend.append('image', imageFile);
    }

    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/admin/products', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await fetchProducts();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      short_description: '',
      price: '',
      compare_price: '',
      stock_quantity: 0,
      sku: '',
      category_id: '',
      is_active: true,
      is_featured: false,
      meta_title: '',
      meta_description: '',
      seo_keywords: ''
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      short_description: product.short_description || '',
      price: product.price,
      compare_price: product.compare_price || '',
      stock_quantity: product.stock_quantity,
      sku: product.sku || '',
      category_id: product.category_id || '',
      is_active: product.is_active,
      is_featured: product.is_featured,
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || '',
      seo_keywords: product.seo_keywords || ''
    });
    // Set image preview if product has an image
    if (product.image_url) {
      setImagePreview(getImageUrl(product.image_url));
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/admin/products/${id}`);
      await fetchProducts();
    } catch (error) {
      alert('Failed to delete product');
    }
  };

  const handleReplenishStock = async (id) => {
    const newStock = prompt('Enter new stock quantity:');
    if (newStock === null || newStock === '') return;
    
    const quantity = parseInt(newStock);
    if (isNaN(quantity) || quantity < 0) {
      alert('Please enter a valid number');
      return;
    }

    try {
      await api.put(`/admin/products/${id}/stock`, { stock_quantity: quantity });
      await fetchProducts();
      alert('Stock updated successfully!');
    } catch (error) {
      alert('Failed to update stock');
    }
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="admin-products">
      <div className="page-header">
        <h1>Products</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
          {showForm ? 'Cancel' : 'Add Product'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="product-form">
          <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Slug *</label>
              <input type="text" name="slug" value={formData.slug} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-group">
            <label>Short Description</label>
            <input type="text" name="short_description" value={formData.short_description} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows="4" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price *</label>
              <input type="number" step="0.01" name="price" value={formData.price} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Compare Price</label>
              <input type="number" step="0.01" name="compare_price" value={formData.compare_price} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Stock Quantity</label>
              <input type="number" name="stock_quantity" value={formData.stock_quantity} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>SKU</label>
              <input type="text" name="sku" value={formData.sku} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Category</label>
            <select name="category_id" value={formData.category_id} onChange={handleChange}>
              <option value="">Select Category</option>
              {categories && categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Image</label>
            <input 
              type="file" 
              name="image" 
              accept="image/*" 
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="image-preview" style={{ marginTop: '10px' }}>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            )}
            {!imagePreview && editingProduct && editingProduct.image_url && (
              <div className="image-preview" style={{ marginTop: '10px' }}>
                <img 
                  src={getImageUrl(editingProduct.image_url)} 
                  alt="Current" 
                  style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Current image</p>
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                Active
              </label>
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleChange} />
                Featured
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Meta Title (SEO)</label>
            <input type="text" name="meta_title" value={formData.meta_title} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Meta Description (SEO)</label>
            <textarea name="meta_description" value={formData.meta_description} onChange={handleChange} rows="2" />
          </div>

          <div className="form-group">
            <label>SEO Keywords</label>
            <input type="text" name="seo_keywords" value={formData.seo_keywords} onChange={handleChange} placeholder="keyword1, keyword2, keyword3" />
          </div>

          <button type="submit" className="btn btn-primary">Save Product</button>
        </form>
      )}

      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>ID</th>
              <th>Name</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products && products.map(product => (
              <tr key={product.id}>
                <td>
                  {product.image_url ? (
                    <img 
                      src={getImageUrl(product.image_url)} 
                      alt={product.name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : (
                    <span style={{ color: '#999', fontSize: '12px' }}>No image</span>
                  )}
                </td>
                <td>{product.id}</td>
                <td>{product.name}</td>
                <td>${parseFloat(product.price || 0).toFixed(2)}</td>
                <td>
                  <span className={`stock-badge ${product.stock_status === 'LOW STOCK' ? 'low-stock' : product.stock_status === 'OUT OF STOCK' ? 'out-of-stock' : 'in-stock'}`}>
                    {product.stock_quantity} {product.stock_status && `(${product.stock_status})`}
                  </span>
                </td>
                <td>
                  <span className={`status ${product.is_active ? 'active' : 'inactive'}`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button onClick={() => handleEdit(product)} className="btn-small">Edit</button>
                  <button onClick={() => handleDelete(product.id)} className="btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProducts;

